import { Checkbox, Container, LinearProgress } from '@mui/material';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { logEvent } from '../../utils/ga';
import { CHECKLIST_CATEGORIES, useChecklist } from './ChecklistPage.hooks';
import styles from './ChecklistPage.module.css';

function ChecklistPage() {
  const {
    checkedIds,
    toggle,
    resetAll,
    overallProgress,
    checkedCount,
    totalItems,
    getCategoryProgress,
  } = useChecklist();

  const handleToggle = (id: string) => {
    toggle(id);
    logEvent('engagement', 'checklist_toggle', id);
  };

  return (
    <div className={styles.root}>
      <Helmet>
        <title>은퇴 준비 체크리스트 — 단계별 노후 준비 점검 | 노후든든</title>
        <meta
          name="description"
          content="은퇴 준비 체크리스트. 국민연금·퇴직연금·개인연금·절세 계좌·지출 계획까지, 노후 준비에 꼭 필요한 항목을 단계별로 점검하세요."
        />
      </Helmet>

      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>은퇴 준비 체크리스트</h1>
        <p className={styles.pageDesc}>은퇴 준비가 어디까지 됐는지 한눈에 확인하세요.</p>
        <div className={styles.progressBarTrack}>
          <div className={styles.progressBarFill} style={{ width: `${overallProgress}%` }} />
        </div>
        <p className={styles.progressLabel}>
          {checkedCount} / {totalItems} 항목 완료
        </p>
      </header>

      <Container maxWidth="sm">
        <div className={styles.overallCard}>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 4px' }}>전체 진행률</p>
          <p className={styles.overallNumbers}>
            {checkedCount} / {totalItems} 완료
          </p>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: '#e5e7eb',
              '& .MuiLinearProgress-bar': { backgroundColor: '#10b981' },
            }}
          />
          <div className={styles.resetBtn}>
            <button
              type="button"
              className={styles.resetBtnInner}
              onClick={() => {
                resetAll();
                logEvent('engagement', 'checklist_reset', 'all');
              }}
            >
              초기화
            </button>
          </div>
        </div>

        {CHECKLIST_CATEGORIES.map((category) => (
          <div key={category.id} className={styles.categoryBlock}>
            <div className={styles.categoryHeader}>
              <h2 className={styles.categoryTitle}>{category.title}</h2>
              <span className={styles.categoryCount}>
                {category.items.filter((item) => checkedIds.has(item.id)).length} / {category.items.length}
              </span>
            </div>
            <div className={styles.categoryProgress}>
              <LinearProgress
                variant="determinate"
                value={getCategoryProgress(category)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#e5e7eb',
                  '& .MuiLinearProgress-bar': { backgroundColor: '#3b82f6' },
                }}
              />
            </div>

            {category.items.map((item) => {
              const checked = checkedIds.has(item.id);

              return (
                <div
                  key={item.id}
                  role="checkbox"
                  aria-checked={checked}
                  tabIndex={0}
                  className={`${styles.itemRow} ${checked ? styles.itemRowChecked : ''}`}
                  onClick={() => handleToggle(item.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToggle(item.id);
                    }
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => handleToggle(item.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0, mt: 0.25, color: '#d1d5db', '&.Mui-checked': { color: '#10b981' } }}
                    inputProps={{ 'aria-label': item.label }}
                  />
                  <div style={{ flex: 1 }}>
                    <p className={`${styles.itemLabel} ${checked ? styles.itemLabelChecked : ''}`}>
                      {item.label}
                    </p>
                    <p className={styles.itemDesc}>{item.description}</p>
                    {item.linkTo && (
                      <Link
                        to={item.linkTo}
                        className={styles.itemLink}
                        onClick={(e) => {
                          e.stopPropagation();
                          logEvent('engagement', 'checklist_link_click', item.linkTo);
                        }}
                      >
                        {item.linkLabel} →
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </Container>
    </div>
  );
}

export default ChecklistPage;
